import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { BrandBadge } from "@/components/BrandMark";
import { money } from "@/lib/constants";
import { CreditCard, Loader2, CheckCircle2, ShieldCheck, FileDown, Landmark, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/invoices/${token}`)
      .then(({ data }) => setInv(data))
      .catch((e) => setError(e.response?.data?.detail || "This payment link is no longer valid"));
  }, [token]);

  const pay = async (method) => {
    setBusy(method);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`,
        { origin_url: window.location.origin, method });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout — please retry");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-royal-deep sidebar-grain px-4 py-10 sm:py-16" data-testid="public-pay-page">
      <div className="mx-auto w-full max-w-[560px]">
        <div className="flex items-center gap-3">
          <BrandBadge />
          <div>
            <p className="font-display text-white text-lg font-semibold leading-none">Cornerstone Ops</p>
            <p className="text-gold text-[10px] tracking-[0.22em] uppercase mt-1">Secure Payment</p>
          </div>
        </div>

        <div className="mt-7 rounded-xl bg-white p-5 sm:p-7">
          {error ? (
            <p className="text-sm text-destructive" data-testid="pay-error">{error}</p>
          ) : !inv ? (
            <Loader2 className="h-6 w-6 animate-spin text-royal" />
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-[0.18em] text-gold font-semibold">Invoice</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-royal-deep mt-1.5 break-words"
                  data-testid="pay-invoice-number">
                {inv.number}
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5 break-words">
                Prepared for {inv.owner_name}
                {inv.period_start ? ` · ${inv.period_start} – ${inv.period_end}` : ""}
              </p>

              <div className="mt-6 divide-y divide-border border-y border-border">
                {inv.line_items.map((i, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-4 py-3"
                       data-testid={`pay-line-${idx}`}>
                    <p className="text-sm text-royal-deep break-words">{i.description}</p>
                    <p className="text-sm font-medium text-royal-deep shrink-0">{money(i.amount)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Balance due</p>
                  <p className="font-display text-3xl font-bold text-royal-deep mt-1" data-testid="pay-balance">
                    {money(inv.balance)}
                  </p>
                </div>
                {inv.due_date && (
                  <p className="text-xs text-muted-foreground">Due upon receipt</p>
                )}
              </div>

              {inv.paid ? (
                <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-emerald-700"
                   data-testid="pay-settled">
                  <CheckCircle2 className="h-4 w-4" /> This invoice is paid in full — thank you.
                </p>
              ) : (
                <>
                  <div className="mt-5 space-y-2.5">
                    <Button data-testid="pay-credit-btn" disabled={!!busy} onClick={() => pay("credit")}
                      className="w-full h-auto py-3.5 flex-col items-start gap-0.5 bg-gold hover:bg-gold/90 text-royal-deep font-semibold">
                      <span className="flex items-center gap-2 text-base">
                        {busy === "credit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                        Pay With Credit Card
                      </span>
                      <span className="text-[11px] font-medium opacity-80">
                        {money(inv.total_charged)} — includes the 2% surcharge ({money(inv.card_fee)})
                      </span>
                    </Button>
                    <Button data-testid="pay-debit-btn" disabled={!!busy} onClick={() => pay("debit")}
                      variant="outline"
                      className="w-full h-auto py-3.5 flex-col items-start gap-0.5 border-royal/40 text-royal hover:bg-royal/5 font-semibold">
                      <span className="flex items-center gap-2 text-base">
                        {busy === "debit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                        Pay With Debit Card
                      </span>
                      <span className="text-[11px] font-medium text-muted-foreground">
                        {money(inv.balance)} — no surcharge
                      </span>
                    </Button>
                    <Button data-testid="pay-bank-btn" disabled={!!busy} onClick={() => pay("bank")}
                      variant="outline"
                      className="w-full h-auto py-3.5 flex-col items-start gap-0.5 border-royal/40 text-royal hover:bg-royal/5 font-semibold">
                      <span className="flex items-center gap-2 text-base">
                        {busy === "bank" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}
                        Pay By Bank Transfer
                      </span>
                      <span className="text-[11px] font-medium text-muted-foreground">
                        {money(inv.balance)} — no surcharge
                      </span>
                    </Button>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground leading-relaxed" data-testid="pay-surcharge-note">
                    The 2% surcharge ({money(inv.card_fee)}) is added in addition to the invoice amount on
                    credit card payments only. Choose debit card or bank transfer and you pay
                    {" "}{money(inv.balance)} with no surcharge.
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{inv.card_note}</p>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Payments are processed securely by Stripe.
                  </p>
                </>
              )}
              <a href={`${process.env.REACT_APP_BACKEND_URL}${inv.statement_url}`} target="_blank"
                 rel="noreferrer" data-testid="pay-statement-link"
                 className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold transition-colors duration-200">
                <FileDown className="h-3.5 w-3.5" /> Download printable {inv.paid ? "receipt" : "statement"} (PDF)
              </a>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-blue-200/50">
          A Christian-Family Owned Company · Outer Banks, North Carolina
        </p>
      </div>
    </div>
  );
}
